import React from 'react';
import MenuButton from './MenuButton';

interface PageHeaderProps {
    icon: string;
    title: string;
    subtitle: string;
    primaryButton?: {
        label: string;
        icon: string;
        onClick: () => void;
    };
    secondaryButton?: {
        label: string;
        icon: string;
        onClick: () => void;
    };
}

const PageHeader: React.FC<PageHeaderProps> = ({
    icon,
    title,
    subtitle,
    primaryButton,
    secondaryButton,
}) => {
    return (
        <header className="h-24 bg-primary border-b border-cyan-600 flex items-center justify-between px-4 md:px-8 shadow-md sticky top-0 z-50 relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-90"></div>
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 flex items-center gap-3 md:gap-6 w-full justify-between">
                <div className="flex items-center gap-3 md:gap-6 min-w-0">
                    <MenuButton />
                    <div className="flex items-center gap-3 md:gap-4 min-w-0">
                        <div className="hidden sm:flex w-10 h-10 rounded-full bg-white/10 items-center justify-center text-white border border-white/20 flex-shrink-0">
                            <i className={`${icon} text-lg`}></i>
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-base md:text-xl font-bold text-white tracking-tight truncate">{title}</h1>
                            {/* The subtitle wraps to several lines in the narrow mobile header. */}
                            <p className="hidden md:block text-cyan-100 text-xs font-medium">{subtitle}</p>
                        </div>
                    </div>
                </div>

                {/* Right Actions */}
                {(secondaryButton || primaryButton) && (
                    <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                        {/* Labels shrink rather than disappear — an icon on its own
                            does not tell the user what the button does. */}
                        {secondaryButton && (
                            <button
                                onClick={secondaryButton.onClick}
                                className="flex items-center gap-1.5 md:gap-2 px-2 lg:px-4 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-[11px] md:text-sm font-medium whitespace-nowrap"
                            >
                                <i className={secondaryButton.icon}></i>
                                <span>{secondaryButton.label}</span>
                            </button>
                        )}

                        {primaryButton && (
                            <button
                                onClick={primaryButton.onClick}
                                className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-6 py-2 md:py-2.5 bg-accent hover:bg-lime-500 text-white rounded font-bold hover:shadow-lg transition-all shadow-md shadow-lime-900/20 border-b-4 border-lime-600 active:border-b-0 active:translate-y-1 whitespace-nowrap text-[11px] md:text-base"
                            >
                                <i className={`${primaryButton.icon} text-sm md:text-lg`}></i>
                                <span>{primaryButton.label}</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
};

export default PageHeader;
